// src/pages/admin/UserManagement.js

import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import './UserManagement.css';
import { apiCall, getRolePrefix, getCurrentUser, saveSession } from '../../utils/api';

const mapRoleName = (roleVal) => roleVal || 'Executive';
const MAX_PROFILE_PHOTO_BYTES = 5 * 1024 * 1024;

/* ─── Overlay wrapper ───────────────────────────────────────────── */
const Modal = ({ onClose, children }) => (
  <div className="modal-overlay" onClick={onClose}>
    <div className="modal-box" onClick={e => e.stopPropagation()}>
      {children}
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════
   0. ADD NEW USER
══════════════════════════════════════════════════════════════════ */
const AddUserModal = ({ onClose, onAdd, shifts, roles }) => {
  const [form, setForm] = useState({
    id: '',
    name: '',
    email: '',
    phone: '',
    password: '',
    role: roles && roles.length > 0 ? roles[0] : 'Executive',
    shiftId: '',
    timezone: 'Asia/Kolkata',
    top: false,
    calendar: false,
    employeeStatus: 'Active',
  });
  const [errors, setErrors] = useState({});

  const set = (key, val) => {
    setForm(prev => ({ ...prev, [key]: val }));
    setErrors(prev => ({ ...prev, [key]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.id.trim()) e.id = 'ID is required.';
    if (!form.name.trim()) e.name = 'Name is required.';
    if (!form.email.trim()) e.email = 'Email is required.';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Enter a valid email.';
    return e;
  };

  const handleCreate = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    onAdd({
      id: form.id.trim(),
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim() || '-',
      password: form.password || 'Welcome@123',
      role: form.role,
      shiftId: form.shiftId || null,
      top: form.top,
      calendar: form.calendar,
      employeeStatus: form.employeeStatus,
    });
  };

  return (
    <Modal onClose={onClose}>
      <h2 className="modal-title">Add New User</h2>

      {/* ID */}
      <div className="form-group">
        <label className="form-label">ID <span className="req">*</span></label>
        <input
          className="form-input"
          placeholder="e.g. EMP006"
          value={form.id}
          onChange={e => set('id', e.target.value)}
        />
        {errors.id && <p className="form-error">{errors.id}</p>}
      </div>

      {/* Name */}
      <div className="form-group">
        <label className="form-label">Name <span className="req">*</span></label>
        <input
          className="form-input"
          value={form.name}
          onChange={e => set('name', e.target.value)}
        />
        {errors.name && <p className="form-error">{errors.name}</p>}
      </div>

      {/* Email */}
      <div className="form-group">
        <label className="form-label">Email <span className="req">*</span></label>
        <input
          className="form-input"
          value={form.email}
          onChange={e => set('email', e.target.value)}
        />
        {errors.email && <p className="form-error">{errors.email}</p>}
      </div>

      {/* Phone */}
      <div className="form-group">
        <label className="form-label">Phone Number</label>
        <input
          className="form-input"
          placeholder="+91 1234567890"
          value={form.phone}
          onChange={e => set('phone', e.target.value)}
        />
      </div>

      {/* Password */}
      <div className="form-group">
        <label className="form-label">Password</label>
        <input
          type="password"
          className="form-input"
          placeholder="Default: Welcome@123"
          value={form.password}
          onChange={e => set('password', e.target.value)}
        />
      </div>

      {/* Profile Photo */}
      <div className="form-group">
        <label className="form-label">Profile Photo</label>
        <input type="file" className="form-input file-input" accept="image/*" />
      </div>

      {/* Role */}
      <div className="form-group">
        <label className="form-label">Role</label>
        <select
          className="form-select"
          value={form.role}
          onChange={e => set('role', e.target.value)}
        >
          {roles.map(r => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </div>

      {/* Shift */}
      <div className="form-group">
        <label className="form-label">Shift</label>
        <select
          className="form-select"
          value={form.shiftId}
          onChange={e => set('shiftId', e.target.value)}
        >
          <option value="">Select Shift</option>
          {shifts.map(s => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
        <p className="form-hint">
          Set the employee's default shift. This will be used automatically when they start tracking time.
        </p>
      </div>

      {/* Timezone */}
      <div className="form-group">
        <label className="form-label">Timezone</label>
        <input
          className="form-input"
          value={form.timezone}
          onChange={e => set('timezone', e.target.value)}
        />
      </div>

      {/* Top Performer */}
      <div className="form-check-group">
        <label className="check-label">
          <input
            type="checkbox"
            checked={form.top}
            onChange={e => set('top', e.target.checked)}
          />
          ⭐ Mark as Top Performer
        </label>
        <p className="form-hint">Top performers will be displayed on the login page.</p>
      </div>

      {/* Calendar Stats */}
      <div className="form-check-group">
        <label className="check-label">
          <input
            type="checkbox"
            checked={form.calendar}
            onChange={e => set('calendar', e.target.checked)}
          />
          Show Calendar Statistics (Weekly/Monthly)
        </label>
        <p className="form-hint">
          Allow this employee to view weekly and monthly calendar statistics in the employee portal.
        </p>
      </div>

      {/* KYC Documents */}
      <div className="form-group">
        <label className="form-label">Onboarding/KYC Documents</label>
        <input type="file" className="form-input file-input" multiple />
      </div>

      {/* Status */}
      <div className="form-group">
        <label className="form-label">Status</label>
        <select
          className="form-select"
          value={form.employeeStatus}
          onChange={e => set('employeeStatus', e.target.value)}
        >
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
          <option value="Relieved">Relieved</option>
          <option value="Long Leave">Long Leave</option>
          <option value="Abscond">Abscond</option>
        </select>
        <p className="form-hint">Only "Active" users can log in. All other statuses will block login access.</p>
      </div>

      {/* Footer */}
      <div className="modal-actions">
        <button className="btn-cancel" onClick={onClose}>Cancel</button>
        <button className="btn-primary-modal" onClick={handleCreate}>Create</button>
      </div>
    </Modal>
  );
};

/* ══════════════════════════════════════════════════════════════════
   1. ASSIGN PROJECT & PROCESS
══════════════════════════════════════════════════════════════════ */
const AssignProjectModal = ({ user, onClose, projects, processes, onSave }) => {
  const [selProjects, setSelProjects] = useState([]);
  const [selProcesses, setSelProcesses] = useState([]);

  const toggle = (arr, setArr, val) =>
    setArr(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);

  const selectAll = (list, setArr) =>
    setArr(prev => prev.length === list.length ? [] : list.map(i => i.id));

  const handleSave = () => {
    onSave(user.id, selProjects, selProcesses);
  };

  return (
    <Modal onClose={onClose}>
      <h2 className="modal-title">Assign Projects &amp; Processes to {user.name}</h2>

      <div className="assign-columns">
        <div className="assign-col">
          <div className="assign-col-header">
            <span className="assign-col-icon">📁</span>
            <strong>Projects</strong>
          </div>
          <div className="assign-list">
            <label className="assign-check select-all">
              <input
                type="checkbox"
                checked={selProjects.length === projects.length && projects.length > 0}
                onChange={() => selectAll(projects, setSelProjects)}
              />
              Select All ({selProjects.length}/{projects.length})
            </label>
            {projects.map(p => (
              <label key={p.id} className="assign-check">
                <input
                  type="checkbox"
                  checked={selProjects.includes(p.id)}
                  onChange={() => toggle(selProjects, setSelProjects, p.id)}
                />
                {p.name}
              </label>
            ))}
          </div>
        </div>

        <div className="assign-col">
          <div className="assign-col-header">
            <span className="assign-col-icon">⚙️</span>
            <strong>Processes</strong>
          </div>
          <div className="assign-list">
            <label className="assign-check select-all">
              <input
                type="checkbox"
                checked={selProcesses.length === processes.length && processes.length > 0}
                onChange={() => selectAll(processes, setSelProcesses)}
              />
              Select All ({selProcesses.length}/{processes.length})
            </label>
            {processes.map(p => (
              <label key={p.id} className="assign-check">
                <input
                  type="checkbox"
                  checked={selProcesses.includes(p.id)}
                  onChange={() => toggle(selProcesses, setSelProcesses, p.id)}
                />
                {p.name}
              </label>
            ))}
          </div>
        </div>
      </div>

      <p className="assign-note">
        📝 Note: Only assigned projects and processes will appear in the employee's dropdown when they log in.
      </p>

      <div className="modal-actions">
        <button className="btn-cancel" onClick={onClose}>Cancel</button>
        <button className="btn-primary-modal" onClick={handleSave}>Save Assignments</button>
      </div>
    </Modal>
  );
};

/* ══════════════════════════════════════════════════════════════════
   2. ASSIGN ROLE
══════════════════════════════════════════════════════════════════ */
const AssignRoleModal = ({ user, onClose, onAssign, roles }) => {
  const [role, setRole] = useState('');

  const handleAssign = () => {
    if (!role) { alert('Please select a role.'); return; }
    onAssign(user.id, role);
  };

  return (
    <Modal onClose={onClose}>
      <h2 className="modal-title">Assign Role to {user.name}</h2>

      <div className="form-group">
        <label className="form-label">Select Role</label>
        <select className="form-select" value={role} onChange={e => setRole(e.target.value)}>
          <option value="">-- Select Role --</option>
          {roles.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      <div className="modal-actions">
        <button className="btn-cancel" onClick={onClose}>Cancel</button>
        <button className="btn-primary-modal" onClick={handleAssign}>Assign Role</button>
      </div>
    </Modal>
  );
};

/* ══════════════════════════════════════════════════════════════════
   3. IMPERSONATE USER
══════════════════════════════════════════════════════════════════ */
const ImpersonateModal = ({ user, onClose, onContinue }) => {
  const [starting, setStarting] = useState(false);
  const handleContinue = async () => {
    if (starting) return;
    setStarting(true);
    try {
      await onContinue(user.id);
    } finally {
      setStarting(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <h2 className="modal-title">👤 Impersonate User</h2>

      <div className="impersonate-info">
        <p className="impersonate-label">You are about to impersonate:</p>
        <div className="impersonate-user-card">
          <strong>{user.name}</strong>
          <span>{user.email}</span>
        </div>
        <div className="impersonate-warning">
          ⚠️ The employee portal will open in a new window. All actions will be logged with your admin account.
        </div>
      </div>

      <div className="modal-actions">
        <button className="btn-cancel" onClick={onClose}>Cancel</button>
        <button className="btn-primary-modal" onClick={handleContinue} disabled={starting}>{starting ? 'Starting session...' : 'Continue'}</button>
      </div>
    </Modal>
  );
};

/* ══════════════════════════════════════════════════════════════════
   4. SET PASSWORD
══════════════════════════════════════════════════════════════════ */
const SetPasswordModal = ({ user, onClose, onSet }) => {
  const [pw, setPw] = useState('');
  const [cpw, setCpw] = useState('');
  const [err, setErr] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showCpw, setShowCpw] = useState(false);

  const handleSet = () => {
    if (pw.length < 6) { setErr('Password must be at least 6 characters.'); return; }
    if (pw !== cpw) { setErr('Passwords do not match.'); return; }
    onSet(user.id, pw);
  };

  return (
    <Modal onClose={onClose}>
      <h2 className="modal-title">Set Password — {user.name}</h2>

      <div className="form-group">
        <label className="form-label">New Password <span className="req">*</span></label>
        <div className="pwd-input-wrap">
          <input
            type={showPw ? 'text' : 'password'}
            className="form-input"
            placeholder="Enter new password (min 6 characters)"
            value={pw}
            onChange={e => { setPw(e.target.value); setErr(''); }}
          />
          <button type="button" className="pwd-toggle-btn" onClick={() => setShowPw(v => !v)} tabIndex={-1}>
            {showPw ? '🙈' : '👁'}
          </button>
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Confirm Password <span className="req">*</span></label>
        <div className="pwd-input-wrap">
          <input
            type={showCpw ? 'text' : 'password'}
            className="form-input"
            placeholder="Confirm new password"
            value={cpw}
            onChange={e => { setCpw(e.target.value); setErr(''); }}
          />
          <button type="button" className="pwd-toggle-btn" onClick={() => setShowCpw(v => !v)} tabIndex={-1}>
            {showCpw ? '🙈' : '👁'}
          </button>
        </div>
      </div>

      {err && <p className="form-error">{err}</p>}

      <div className="modal-actions">
        <button className="btn-cancel" onClick={onClose}>Cancel</button>
        <button className="btn-primary-modal" onClick={handleSet}>Set Password</button>
      </div>
    </Modal>
  );
};

/* ══════════════════════════════════════════════════════════════════
   5. RESET PASSWORD
══════════════════════════════════════════════════════════════════ */
const ResetPasswordModal = ({ user, onClose }) => {
  const handleReset = () => {
    alert(`Password reset link sent to ${user.email}.`);
    onClose();
  };

  return (
    <Modal onClose={onClose}>
      <h2 className="modal-title">🔄 Reset Password</h2>

      <div className="impersonate-info">
        <p className="impersonate-label">A password reset link will be sent to:</p>
        <div className="impersonate-user-card">
          <strong>{user.name}</strong>
          <span>{user.email}</span>
        </div>
        <div
          className="impersonate-warning"
          style={{ background: '#e0f2fe', borderColor: '#0ea5e9', color: '#0c4a6e' }}
        >
          ℹ️ The user will receive an email with instructions to reset their password.
        </div>
      </div>

      <div className="modal-actions">
        <button className="btn-cancel" onClick={onClose}>Cancel</button>
        <button className="btn-primary-modal" onClick={handleReset}>Send Reset Link</button>
      </div>
    </Modal>
  );
};

/* ══════════════════════════════════════════════════════════════════
   6. EDIT USER
══════════════════════════════════════════════════════════════════ */
const EditUserModal = ({ user, onClose, onUpdate, shifts, roles }) => {
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveStage, setSaveStage] = useState('');
  const [form, setForm] = useState({
    name: user.name,
    email: user.email,
    phone: user.phone === '-' ? '' : user.phone,
    role: user.role,
    shiftId: user.shiftId || '',
    timezone: user.timezone || 'Asia/Kolkata',
    top: user.top,
    calendar: user.calendar ?? true,
    employeeStatus: user.employeeStatus || (user.status === 'Active' ? 'Active' : 'Inactive'),
  });

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleUpdate = async () => {
    if (saving) return;
    setSaving(true);
    let currentStage = 'updating user';
    setSaveStage('Updating user...');
    try {
      let profilePhotoUrl = user.profilePhotoUrl || null;

      if (profilePhoto) {
        if (!profilePhoto.type || !profilePhoto.type.startsWith('image/')) {
          throw new Error('Choose a valid image file for the profile photo.');
        }
        if (profilePhoto.size > MAX_PROFILE_PHOTO_BYTES) {
          throw new Error('Profile photo must be 5 MB or smaller. Please choose a smaller image.');
        }
        currentStage = 'uploading photo';
        setSaveStage('Uploading photo...');
        const uploadData = new FormData();
        uploadData.append('file', profilePhoto);
        uploadData.append('entityType', 'profile');
        uploadData.append('entityId', user.id);

        const uploaded = await apiCall('/media/upload', 'POST', uploadData);
        profilePhotoUrl = uploaded.url;
      }

      currentStage = 'updating user';
      setSaveStage('Updating user...');
      await onUpdate({
        ...user,
        name: form.name,
        email: form.email,
        phone: form.phone || '-',
        role: form.role,
        originalRole: user.role,
        shiftId: form.shiftId || null,
        originalShiftId: user.shiftId || null,
        top: form.top,
        calendar: form.calendar,
        timezone: form.timezone,
        employeeStatus: form.employeeStatus,
        status: form.employeeStatus,
        initial: form.name.charAt(0).toUpperCase(),
        profilePhotoUrl,
      });
    } catch (err) {
      alert(`Error ${currentStage}: ${err.message}`);
    } finally {
      setSaving(false);
      setSaveStage('');
    }
  };

  return (
    <Modal onClose={onClose}>
      <h2 className="modal-title">Edit User</h2>

      <div className="form-group">
        <label className="form-label">ID</label>
        <input className="form-input" value={user.userCode} disabled style={{ backgroundColor: '#f1f5f9', cursor: 'not-allowed', color: '#64748b' }} />
      </div>

      <div className="form-group">
        <label className="form-label">Name <span className="req">*</span></label>
        <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} />
      </div>

      <div className="form-group">
        <label className="form-label">Email</label>
        <input className="form-input" value={form.email} onChange={e => set('email', e.target.value)} />
      </div>

      <div className="form-group">
        <label className="form-label">Phone Number</label>
        <input className="form-input" placeholder="+91 1234567890" value={form.phone} onChange={e => set('phone', e.target.value)} />
      </div>

      <div className="form-group">
        <label className="form-label">Profile Photo</label>
        <input
          type="file"
          className="form-input file-input"
          accept="image/*"
          onChange={e => setProfilePhoto(e.target.files[0] || null)}
        />
        <p className="form-hint">Use JPG or PNG up to 5 MB.</p>
        {user.profilePhotoUrl && <p className="form-hint">A photo is already uploaded. Choose a new image to replace it.</p>}
      </div>

      <div className="form-group">
        <label className="form-label">Role</label>
        <select className="form-select" value={form.role} onChange={e => set('role', e.target.value)}>
          {roles.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label">Shift</label>
        <select className="form-select" value={form.shiftId} onChange={e => set('shiftId', e.target.value)}>
          <option value="">-- None --</option>
          {shifts.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <p className="form-hint">Set the employee's default shift. This will be used automatically when they start tracking time.</p>
      </div>

      <div className="form-group">
        <label className="form-label">Timezone</label>
        <input className="form-input" value={form.timezone} onChange={e => set('timezone', e.target.value)} />
      </div>

      <div className="form-check-group">
        <label className="check-label">
          <input type="checkbox" checked={form.top} onChange={e => set('top', e.target.checked)} />
          ⭐ Mark as Top Performer
        </label>
        <p className="form-hint">Top performers will be displayed on the login page.</p>
      </div>

      <div className="form-check-group">
        <label className="check-label">
          <input type="checkbox" checked={form.calendar} onChange={e => set('calendar', e.target.checked)} />
          Show Calendar Statistics (Weekly/Monthly)
        </label>
        <p className="form-hint">Allow this employee to view weekly and monthly calendar statistics in the employee portal.</p>
      </div>

      <div className="form-group">
        <label className="form-label">Onboarding/KYC Documents</label>
        <input type="file" className="form-input file-input" multiple />
      </div>

      {/* Status */}
      <div className="form-group">
        <label className="form-label">Status</label>
        <select
          className="form-select"
          value={form.employeeStatus}
          onChange={e => set('employeeStatus', e.target.value)}
        >
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
          <option value="Relieved">Relieved</option>
          <option value="Long Leave">Long Leave</option>
          <option value="Abscond">Abscond</option>
        </select>
        <p className="form-hint">Only "Active" users can log in. All other statuses will block login access.</p>
      </div>

      <div className="modal-actions">
        <button className="btn-cancel" onClick={onClose}>Cancel</button>
        <button className="btn-primary-modal" onClick={handleUpdate} disabled={saving}>
          {saving ? saveStage : 'Update'}
        </button>
      </div>
    </Modal>
  );
};

/* ══════════════════════════════════════════════════════════════════
   7. DELETE USER
══════════════════════════════════════════════════════════════════ */
const DeleteModal = ({ user, onClose, onDelete }) => {
  const handleDelete = () => {
    onDelete(user.id);
  };

  return (
    <Modal onClose={onClose}>
      <div className="delete-modal">
        <div className="delete-icon">🗑️</div>
        <h2 className="modal-title">Delete User</h2>
        <p className="delete-msg">
          Are you sure you want to delete <strong>{user.name}</strong>?<br />
          This action cannot be undone.
        </p>
        <div className="modal-actions centered">
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
          <button className="btn-danger-modal" onClick={handleDelete}>Delete</button>
        </div>
      </div>
    </Modal>
  );
};

/* ══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════ */
const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [modal, setModal] = useState(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);

  const location = useLocation();

  const currentUser = getCurrentUser();
  const hasPermission = (perm) => currentUser?.roles?.includes('Admin') || currentUser?.permissions?.includes(perm);

  // ── Load users from API ──────────────────────────────────────
  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiCall('/users');
      const mapped = data.map(u => ({
        id: u.id,
        userCode: u.userCode,
        initial: u.fullName ? u.fullName.charAt(0).toUpperCase() : '?',
        name: u.fullName,
        profilePhotoUrl: u.profilePhotoUrl || null,
        email: u.email,
        phone: u.phone || '-',
        timezone: u.timezone || 'Asia/Kolkata',
        calendar: u.showCalendarStats ?? true,
        role: u.role || 'Executive',
        shiftId: u.shiftId || null,
        shift: u.shift || '-',
        top: u.isTopPerformer,
        status: u.employeeStatus || (u.isActive ? 'Active' : 'Inactive'),
        employeeStatus: u.employeeStatus || (u.isActive ? 'Active' : 'Inactive'),
      }));
      const rolePriority = {
        'admin': 1,
        'manager': 2,
        'team leader': 3,
        'employee': 4,
        'viewer': 5
      };
      mapped.sort((a, b) => {
        const priorityA = rolePriority[a.role.toLowerCase()] || 99;
        const priorityB = rolePriority[b.role.toLowerCase()] || 99;
        if (priorityA !== priorityB) {
          return priorityA - priorityB;
        }
        return (a.userCode || '').localeCompare(b.userCode || '', undefined, { numeric: true, sensitivity: 'base' });
      });
      setUsers(mapped);
    } catch (err) {
      setError('Failed to load users: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Load dropdown data from API ──────────────────────────────
  const loadDropdownData = useCallback(async () => {
    try {
      const [proj, proc, shift, rolesData] = await Promise.all([
        apiCall('/projects'),
        apiCall('/processes'),
        apiCall('/shifts'),
        apiCall('/roles'),
      ]);
      setProjects(proj.map(p => ({ id: p.id, name: p.name })));
      setProcesses(proc.map(p => ({ id: p.id, name: p.name })));
      setShifts(shift.map(s => ({ id: s.id, name: s.name })));
      setAvailableRoles(rolesData.map(r => r.name));
    } catch (err) {
      console.warn('Failed to load dropdown data:', err.message);
    }
  }, []);

  useEffect(() => {
    loadUsers();
    loadDropdownData();
  }, [loadUsers, loadDropdownData]);

  useEffect(() => {
    if (location.state?.openAddUser) {
      setShowAddUser(true);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const open = (type, user) => setModal({ type, user });
  const close = () => setModal(null);

  // ── API-backed handlers ──────────────────────────────────────

  const handleAdd = async (formData) => {
    try {
      await apiCall('/users', 'POST', {
        userCode: formData.id,
        fullName: formData.name,
        email: formData.email,
        // Fix: treat empty string as null too
        phone: formData.phone && formData.phone !== '-' ? formData.phone : null,
        password: formData.password || 'Welcome@123',
        roleName: mapRoleName(formData.role),
        shiftId: formData.shiftId || null,
        isTopPerformer: formData.top,
        showCalendarStats: formData.calendar,
        isActive: formData.employeeStatus === 'Active',
        employeeStatus: formData.employeeStatus,
      });
      await loadUsers();
      setShowAddUser(false);
    } catch (err) {
      alert('Error creating user: ' + err.message);
    }
  };

  const handleUpdate = async (updatedUser) => {
    const payload = {
      fullName: updatedUser.name,
      email: updatedUser.email,
      phone: updatedUser.phone !== '-' ? updatedUser.phone : null,
      timezone: updatedUser.timezone || null,
      isTopPerformer: updatedUser.top,
      showCalendarStats: updatedUser.calendar,
      isActive: updatedUser.employeeStatus === 'Active',
      employeeStatus: updatedUser.employeeStatus,
    };

    if (mapRoleName(updatedUser.role) !== mapRoleName(updatedUser.originalRole || updatedUser.role)) {
      payload.roleName = mapRoleName(updatedUser.role);
    }

    const previousShiftId = updatedUser.originalShiftId || null;
    const nextShiftId = updatedUser.shiftId || null;
    if (previousShiftId !== nextShiftId) {
      if (nextShiftId) {
        payload.shiftId = nextShiftId;
      } else {
        payload.clearShift = true;
      }
    }

    const savedUser = await apiCall(`/users/${updatedUser.id}`, 'PUT', payload);
    const mappedSavedUser = {
      id: savedUser.id,
      userCode: savedUser.userCode,
      initial: savedUser.fullName ? savedUser.fullName.charAt(0).toUpperCase() : '?',
      name: savedUser.fullName,
      profilePhotoUrl: savedUser.profilePhotoUrl || updatedUser.profilePhotoUrl || null,
      email: savedUser.email,
      phone: savedUser.phone || '-',
      timezone: savedUser.timezone || 'Asia/Kolkata',
      calendar: savedUser.showCalendarStats ?? true,
      role: savedUser.roles?.[0] || updatedUser.role || 'Executive',
      shiftId: savedUser.currentShiftId || updatedUser.shiftId || null,
      shift: savedUser.currentShift || updatedUser.shift || '-',
      top: savedUser.isTopPerformer,
      status: savedUser.employeeStatus || (savedUser.isActive ? 'Active' : 'Inactive'),
      employeeStatus: savedUser.employeeStatus || (savedUser.isActive ? 'Active' : 'Inactive'),
    };
    setUsers(prev => prev.map(u => u.id === updatedUser.id ? mappedSavedUser : u));
    if (getCurrentUser()?.userId === updatedUser.id) {
      sessionStorage.setItem('user', JSON.stringify({
        ...getCurrentUser(),
        fullName: savedUser.fullName,
        email: savedUser.email,
        profilePhotoUrl: savedUser.profilePhotoUrl || null,
      }));
      window.dispatchEvent(new Event('user_profile_updated'));
    }
    close();
    loadUsers().catch(err => {
      console.warn('User saved, but refreshing the user list failed:', err.message);
    });
  };

  const handleDelete = async (userId) => {
    try {
      await apiCall(`/users/${userId}`, 'DELETE');
      await loadUsers();
      close();
    } catch (err) {
      alert('Error deleting user: ' + err.message);
    }
  };

  const handleSetPassword = async (userId, newPassword) => {
    try {
      await apiCall(`/users/${userId}/set-password`, 'POST', {
        newPassword,
        confirmPassword: newPassword,
      });
      alert('Password updated successfully.');
      close();
    } catch (err) {
      alert('Error setting password: ' + err.message);
    }
  };

  const handleAssignRole = async (userId, roleName) => {
    try {
      await apiCall(`/users/${userId}/assign-role`, 'POST', { roleName });
      await loadUsers();
      alert(`Role "${roleName}" assigned successfully.`);
      close();
    } catch (err) {
      alert('Error assigning role: ' + err.message);
    }
  };

  const handleAssignProjects = async (userId, projectIds, processIds) => {
    try {
      await apiCall(`/users/${userId}/assign-projects`, 'POST', {
        projectIds,
        processIds,
      });
      alert('Assignments saved successfully.');
      close();
    } catch (err) {
      alert('Error saving assignments: ' + err.message);
    }
  };

  const handleImpersonate = async (userId) => {
    const impersonationWindow = window.open('', '_blank');
    if (!impersonationWindow) {
      alert('Please allow pop-ups to impersonate a user.');
      return;
    }
    try {
      const data = await apiCall(`/auth/impersonate/${userId}`, 'POST');
      if (impersonationWindow.closed) throw new Error('The impersonation window was closed.');
      saveSession(data, impersonationWindow.sessionStorage);
      impersonationWindow.sessionStorage.setItem('isImpersonating', 'true');
      const prefix = getRolePrefix(data.roles);
      impersonationWindow.location.replace(`/workwise/${prefix}/dashboard`);
      close();
    } catch (err) {
      if (impersonationWindow && !impersonationWindow.closed) impersonationWindow.close();
      alert('Error impersonating user: ' + err.message);
    }
  };

  // ── Top scroll sync refs ─────────────────────────────────────
  const topScrollRef = React.useRef(null);
  const bottomScrollRef = React.useRef(null);

  useEffect(() => {
    const topEl = topScrollRef.current;
    const bottomEl = bottomScrollRef.current;
    if (!topEl || !bottomEl) return;

    const resizeObserver = new ResizeObserver(() => {
      const firstChild = bottomEl.firstElementChild;
      if (firstChild) {
        const tableWidth = firstChild.offsetWidth;
        const innerDummy = topEl.firstElementChild;
        if (innerDummy) innerDummy.style.width = `${tableWidth}px`;
      }
    });
    resizeObserver.observe(bottomEl);

    let isSyncingTop = false, isSyncingBottom = false;
    const handleTopScroll = () => {
      if (!isSyncingBottom) {
        isSyncingTop = true;
        bottomEl.scrollLeft = topEl.scrollLeft;
        isSyncingTop = false;
      }
    };
    const handleBottomScroll = () => {
      if (!isSyncingTop) {
        isSyncingBottom = true;
        topEl.scrollLeft = bottomEl.scrollLeft;
        isSyncingBottom = false;
      }
    };
    topEl.addEventListener('scroll', handleTopScroll);
    bottomEl.addEventListener('scroll', handleBottomScroll);
    return () => {
      resizeObserver.disconnect();
      topEl.removeEventListener('scroll', handleTopScroll);
      bottomEl.removeEventListener('scroll', handleBottomScroll);
    };
  }, [users]);

  // Pagination calculations
  const totalItems = users.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedUsers = users.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // ── Render ───────────────────────────────────────────────────
  if (loading) return (
    <div className="user-mgmt-container">
      <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>
        Loading users...
      </div>
    </div>
  );

  if (error) return (
    <div className="user-mgmt-container">
      <div style={{ padding: '40px', textAlign: 'center', color: 'red' }}>
        {error}
      </div>
    </div>
  );

  return (
    <div className="user-mgmt-container">

      {/* ── Header ── */}
      <div className="mgmt-header">
        <div className="header-left">
          <span className="mgmt-icon">👥</span>
          <h2>User Management</h2>
        </div>
        {hasPermission('employees.create') && (
          <button className="add-user-btn" onClick={() => setShowAddUser(true)}>
            + Add User
          </button>
        )}
      </div>

      {/* ── Pagination ── */}
      <div className="pm-pagination" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '12px', padding: '0 4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontSize: '0.85rem', color: '#4a5568' }}>Items per page:</label>
          <select
            value={itemsPerPage}
            onChange={e => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            style={{
              padding: '4px 8px',
              border: '1px solid #cbd5e1',
              borderRadius: '6px',
              outline: 'none',
              fontSize: '0.85rem',
              backgroundColor: '#fff'
            }}
          >
            {[10, 25, 50, 100].map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(p => p - 1)}
                style={{
                  padding: '5px 12px',
                  background: currentPage === 1 ? '#e2e8f0' : '#ffffff',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  color: currentPage === 1 ? '#94a3b8' : '#334155',
                  fontSize: '0.8rem',
                  fontWeight: 600
                }}
              >‹ Prev</button>
              <span style={{ color: '#475569', fontSize: '0.85rem', fontWeight: 500 }}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(p => p + 1)}
                style={{
                  padding: '5px 12px',
                  background: currentPage === totalPages ? '#e2e8f0' : '#ffffff',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  color: currentPage === totalPages ? '#94a3b8' : '#334155',
                  fontSize: '0.8rem',
                  fontWeight: 600
                }}
              >Next ›</button>
            </div>
          )}
          <span style={{ fontSize: '0.85rem', color: '#4a5568', fontWeight: 500 }}>
            Showing {totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} items
          </span>
        </div>
      </div>

      {/* ── Top Scrollbar ── */}
      <div className="double-scroll-top" ref={topScrollRef}>
        <div className="double-scroll-top-inner" />
      </div>

      {/* ── Table ── */}
      <div className="table-wrapper" ref={bottomScrollRef}>
        <table className="user-table">
          <thead>
            <tr>
              <th>Photo</th>
              <th>ID</th>
              <th className="th-name">Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Role</th>
              <th>Shift</th>
              <th>Top Performer</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedUsers.length === 0 ? (
              <tr>
                <td colSpan="10" style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                  No users found. Click "+ Add User" to create one.
                </td>
              </tr>
            ) : paginatedUsers.map(user => (
              <tr key={user.id}>
                <td>
                  {user.profilePhotoUrl ? (
                    <img
                      src={user.profilePhotoUrl.startsWith('http') ? user.profilePhotoUrl : `${process.env.REACT_APP_API_URL || 'https://arrowdatatech.com/api'}${user.profilePhotoUrl}`}
                      alt={user.name}
                      className="avatar-circle"
                      style={{ width: '38px', height: '38px', borderRadius: '50%', objectFit: 'cover', display: 'block' }}
                    />
                  ) : (
                    <div className="avatar-circle">{user.initial}</div>
                  )}
                </td>
                <td className="user-code-col">
                  <strong>{user.userCode}</strong>
                </td>
                <td className="td-name col-left">{user.name}</td>
                <td>{user.email}</td>
                <td>{user.phone}</td>
                <td>
                  <span className={`role-badge ${user.role.toLowerCase().replace(/\s+/g, '-')}`}>
                    {user.role}
                  </span>
                </td>
                <td>{user.shift}</td>
                <td>
                  {user.top && (
                    <span className="top-performer">⭐ Top Performer</span>
                  )}
                </td>
                <td>
                  <span className={`status-badge ${user.status.toLowerCase().replace(/\s+/g, '-')}`}>
                    {user.status}
                  </span>
                </td>

                {/* ── Action Dropdown ── */}
                <td>
                  <select
                    className="action-dropdown"
                    defaultValue=""
                    onChange={e => {
                      const action = e.target.value;
                      if (action) open(action, user);
                      e.target.value = '';
                    }}
                  >
                    <option value="" disabled>⚙️ Actions</option>
                    {hasPermission('employees.update') && <option value="assign">🗒️ Assign Project &amp; Process</option>}
                    {hasPermission('employees.manage_roles') && <option value="role">🔐 Assign Role</option>}
                    {currentUser?.roles?.includes('Admin') && <option value="impersonate">👤 Impersonate User</option>}
                    {hasPermission('employees.update') && <option value="setpw">🔑 Set Password</option>}
                    {hasPermission('employees.update') && <option value="resetpw">🔄 Reset Password</option>}
                    {hasPermission('employees.update') && <option value="edit">✏️ Edit User</option>}
                    {hasPermission('employees.delete') && <option value="delete">🗑️ Delete User</option>}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Add User Modal ── */}
      {showAddUser && (
        <AddUserModal
          onClose={() => setShowAddUser(false)}
          onAdd={handleAdd}
          shifts={shifts}
          roles={availableRoles}
        />
      )}

      {/* ── Action Modals ── */}
      {modal?.type === 'assign' && (
        <AssignProjectModal
          user={modal.user}
          onClose={close}
          projects={projects}
          processes={processes}
          onSave={handleAssignProjects}
        />
      )}
      {modal?.type === 'role' && (
        <AssignRoleModal
          user={modal.user}
          onClose={close}
          onAssign={handleAssignRole}
          roles={availableRoles}
        />
      )}
      {modal?.type === 'impersonate' && (
        <ImpersonateModal
          user={modal.user}
          onClose={close}
          onContinue={handleImpersonate}
        />
      )}
      {modal?.type === 'setpw' && (
        <SetPasswordModal
          user={modal.user}
          onClose={close}
          onSet={handleSetPassword}
        />
      )}
      {modal?.type === 'resetpw' && (
        <ResetPasswordModal
          user={modal.user}
          onClose={close}
        />
      )}
      {modal?.type === 'edit' && (
        <EditUserModal
          user={modal.user}
          onClose={close}
          onUpdate={handleUpdate}
          shifts={shifts}
          roles={availableRoles}
        />
      )}
      {modal?.type === 'delete' && (
        <DeleteModal
          user={modal.user}
          onClose={close}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

export default UserManagement;
